import { ChevronRight, Cube } from "@procore/core-icons";
import { useMemo } from "react";

import type { ListNode, Node, Tree } from "components/objectTree/types";
import { useHidden } from "contexts/hidden";
import type { ModelTreeNodeData } from "features/modelTree/types";
import { isFileNode } from "utils/objectPath";
import { getGroupStateForIdSet } from "utils/treeHelpers";

import {
    ITEM_HEIGHT_IOS,
    MOBILE_ROW_ICON_TO_LABEL_GAP,
    MOBILE_ROW_LABEL_TO_TOGGLE_GAP,
    MOBILE_ROW_TOGGLE_TO_CHEVRON_GAP,
} from "../constants";
import { invertGroupState, useIsolationVisibility } from "../hooks/useIsolationVisibility";
import { formatNodeDisplayName } from "../utils/formatting";
import { VisibilityToggleButton } from "./VisibilityToggleButton";

export type MobileObjectTreeRowProps = {
    node: Node<ModelTreeNodeData>;
    tree: Tree<ModelTreeNodeData>;
    onDrillInto: (path: string) => void;
    onLeafTap: (node: Node<ModelTreeNodeData>) => void;
};

export function MobileObjectTreeRow({ node, tree, onDrillInto, onLeafTap }: MobileObjectTreeRowProps) {
    const hidden = useHidden();
    const isolation = useIsolationVisibility();
    const isolationVisibleIds = isolation.visibleIds;

    const item = useMemo((): ListNode<ModelTreeNodeData> => ({ node, parent: null, level: 0 }), [node]);
    const hiddenState = useMemo(() => {
        if (isolationVisibleIds) {
            return invertGroupState(getGroupStateForIdSet(isolationVisibleIds, item, tree));
        }
        return getGroupStateForIdSet(hidden.ids, item, tree);
    }, [hidden.ids, isolationVisibleIds, item, tree]);
    const isHidden =
        hiddenState === "all" || (!isolationVisibleIds && !node.isLeaf && hidden.ids.has(node.data.object.id));

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (node.isLeaf) {
            onLeafTap(node);
        } else {
            onDrillInto(node.path);
        }
    };

    const displayName = formatNodeDisplayName(item);
    const contentOpacity = isHidden ? 0.4 : 1;

    return (
        <div
            onClick={handleClick}
            style={{
                display: "flex",
                alignItems: "center",
                minHeight: ITEM_HEIGHT_IOS,
                paddingTop: 12,
                paddingBottom: 12,
                paddingLeft: 16,
                paddingRight: 16,
                position: "relative",
                userSelect: "none",
            }}
        >
            {isFileNode(node.path) && (
                <Cube
                    style={{
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                        marginRight: MOBILE_ROW_ICON_TO_LABEL_GAP,
                        color: "#232729",
                        opacity: contentOpacity,
                    }}
                />
            )}
            <span
                data-testid="row-name"
                style={{
                    flex: 1,
                    minWidth: 0,
                    marginRight: MOBILE_ROW_LABEL_TO_TOGGLE_GAP,
                    overflowWrap: "anywhere",
                    fontSize: 18,
                    fontWeight: 600,
                    color: "#171a1c",
                    letterSpacing: "-0.28px",
                    lineHeight: "22px",
                    opacity: contentOpacity,
                }}
            >
                {displayName}
            </span>
            <VisibilityToggleButton node={node} tree={tree} />
            {!node.isLeaf && (
                <ChevronRight
                    style={{
                        flexShrink: 0,
                        width: 20,
                        height: 20,
                        marginLeft: MOBILE_ROW_TOGGLE_TO_CHEVRON_GAP,
                        color: "#ACB5B9",
                    }}
                />
            )}
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 16,
                    right: 16,
                    height: 1,
                    backgroundColor: "rgba(172, 181, 185, 0.32)",
                }}
            />
        </div>
    );
}
